import { portfolioHomeFiltersConfig } from 'features/pages/portfolio/Portfolio/portfolioFilters.config';
import { BaseFiltersConfig, isOption, MultiSelectFilterConfigEntry, Option } from 'ui-kit';
import { isArrayOf } from 'utils/typeUtils';

import { FilterableField } from '__generated__/globalTypes';

type FilterEntry = BaseFiltersConfig[string];

const multiSelect = (
  displayName: string,
  field: FilterableField,
): MultiSelectFilterConfigEntry<Option> => ({
  type: 'select-multi',
  displayName,
  filterableFieldName: field,
  filterOptions: [],
  getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
  getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
  valueToString: (options) =>
    isArrayOf(isOption)(options) ? options.map((option) => option.label).join(', ') : '',
});

const fromPortfolio = (field: keyof typeof portfolioHomeFiltersConfig): FilterEntry =>
  portfolioHomeFiltersConfig[field] as FilterEntry;

// Every field a sale's loan table can render a control for. Which of them a viewer gets is the
// page's allowed_filter_fields alone: a field missing here is not offered even when allowed.
const SALE_LOAN_FILTER_ENTRIES: Partial<Record<FilterableField, FilterEntry>> = {
  [FilterableField.account_id]: fromPortfolio(FilterableField.account_id),
  [FilterableField.sale_key]: multiSelect('Sale Key', FilterableField.sale_key),
  [FilterableField.fund_number]: multiSelect('Fund #', FilterableField.fund_number),
  [FilterableField.institution_id]: multiSelect('Institution', FilterableField.institution_id),
  [FilterableField.performance_status]: multiSelect(
    'Performance',
    FilterableField.performance_status,
  ),
  [FilterableField.asset_class]: multiSelect('Asset Class', FilterableField.asset_class),
  [FilterableField.product]: fromPortfolio(FilterableField.product),
  [FilterableField.state]: fromPortfolio(FilterableField.state),
  [FilterableField.current_balance_cents]: fromPortfolio(FilterableField.current_balance_cents),
  [FilterableField.original_balance_cents]: fromPortfolio(FilterableField.original_balance_cents),
  [FilterableField.interest_rate_bps]: fromPortfolio(FilterableField.interest_rate_bps),
  [FilterableField.loan_terms_months]: {
    type: 'range',
    displayName: 'Term',
    min: 1,
    max: 480,
    integer: true,
  },
  [FilterableField.remaining_loan_terms_months]: fromPortfolio(
    FilterableField.remaining_loan_terms_months,
  ),
  [FilterableField.age_months]: fromPortfolio(FilterableField.age_months),
  [FilterableField.first_payment_date_seconds]: fromPortfolio(
    FilterableField.first_payment_date_seconds,
  ),
  [FilterableField.origination_date_seconds]: fromPortfolio(
    FilterableField.origination_date_seconds,
  ),
  [FilterableField.maturity_date_seconds]: fromPortfolio(FilterableField.maturity_date_seconds),
  [FilterableField.next_payment_date_seconds]: fromPortfolio(
    FilterableField.next_payment_date_seconds,
  ),
  [FilterableField.ltv_bps]: fromPortfolio(FilterableField.ltv_bps),
  [FilterableField.loan_number]: { type: 'list-input', displayName: 'Loan Number' },
  [FilterableField.borrower_credit_score]: fromPortfolio(FilterableField.borrower_credit_score),
  [FilterableField.dti_bps]: fromPortfolio(FilterableField.dti_bps),
};

const isMultiSelect = (
  entry: FilterEntry | undefined,
): entry is MultiSelectFilterConfigEntry<Option> => entry?.type === 'select-multi';

// A multi-select lists the values salePoolLoanTerms returns, so only a field allowed both as a
// filter and as a term field gets one: terms are never asked for any other field.
export function saleLoanTermFields(
  allowedFilterFields: readonly FilterableField[],
  allowedTermFields: readonly FilterableField[],
): FilterableField[] {
  return allowedFilterFields.filter(
    (field) => isMultiSelect(SALE_LOAN_FILTER_ENTRIES[field]) && allowedTermFields.includes(field),
  );
}

export function saleLoanFiltersConfig(
  allowedFilterFields: readonly FilterableField[],
  allowedTermFields: readonly FilterableField[],
  terms: ReadonlyMap<FilterableField, readonly string[]>,
): BaseFiltersConfig {
  const termFields = new Set(saleLoanTermFields(allowedFilterFields, allowedTermFields));
  const config: BaseFiltersConfig = {};
  for (const field of allowedFilterFields) {
    const entry = SALE_LOAN_FILTER_ENTRIES[field];
    if (!entry) continue;
    if (!isMultiSelect(entry)) {
      config[field] = entry;
      continue;
    }
    if (!termFields.has(field)) continue;
    const options = [...(terms.get(field) ?? [])]
      .sort((a, b) => a.localeCompare(b))
      .map((term) => ({ label: term, value: term }));
    if (options.length > 0) config[field] = { ...entry, filterOptions: options };
  }
  return config;
}
